import React, { useEffect, useRef, useState } from 'react';
import './ChangeName.scss';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { brightDataCountries } from '../../../utils/utils';
import { useAccount } from '../../../contexts/account/AccountContexts';
import DropdownItem from '../../dropdown-item/DropdownItem';

interface ChangeNameProps {
  selectedProjectLabel: string;
  selectedCountry: string | null;
  onChangeProjectLabel: (projectLabel: string) => void;
  onChangeProjectCountry: (country: string | null) => void;
  isSaveDisabled: (disabled: boolean) => void;
}

const ChangeName: React.FC<ChangeNameProps> = ({
  selectedProjectLabel,
  selectedCountry,
  onChangeProjectLabel,
  onChangeProjectCountry,
  isSaveDisabled,
}) => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const [country, setCountry] = useState<string | null>(selectedCountry);
  const [isRequiredError, setRequiredError] = useState<boolean>(true);
  const projectName = useRef<HTMLInputElement | null>(null);

  let isFirstLoad = true;

  useEffect(() => {
    setCountry(selectedCountry);
  }, [selectedCountry]);

  useEffect(() => {
    if (projectName.current) {
      projectName.current.value = selectedProjectLabel;
    }
    setRequiredError(true);
    isFirstLoad = true;
  }, [selectedProjectLabel, selectedCountry]);

  useEffect(() => {
    isSaveDisabled(isRequiredError);
  }, [isRequiredError]);

  const isProjectLabelChanged =
    selectedProjectLabel.toLowerCase().trim() !== projectName.current?.value.toLowerCase().trim() ||
    country !== selectedCountry;

  const handleOnChange = async () => {
    isFirstLoad = false;
    const isInvalid = projectName.current?.value.trim() === '' || !isProjectLabelChanged;
    setRequiredError(isInvalid);

    if (!isInvalid) {
      onChangeProjectLabel(projectName.current?.value || '');
    }
  };

  const handleOnChangeCountry = async (code: string | null) => {
    onChangeProjectCountry(code);
    setCountry(code);

    const isInvalid = projectName.current?.value.trim() === '' || !isProjectLabelChanged;
    setRequiredError(isInvalid);
  };

  return (
    <div className="change-name-dialog">
      <div className="change-name-dialog__input">
        <div className="form-box__label">{t('projectName')}:</div>
        <InputText
          ref={projectName}
          type="text"
          id="projectName"
          defaultValue={selectedProjectLabel}
          onInput={handleOnChange}
          className={isRequiredError && !isFirstLoad ? 'form-box__invalid' : ''}
        />
        {accountResponse?.addons.includes('GEO') && (
          <>
            <div className="form-box__label">{t('geolocation')}:</div>
            <Dropdown
              value={brightDataCountries.find((option) => option.code === country) || null}
              onChange={(e) => handleOnChangeCountry(e.value.code || null)}
              placeholder={t('selectCountry')}
              options={brightDataCountries}
              className="dropdown"
              optionLabel="name"
              itemTemplate={(option) => <DropdownItem icon={option.icon} name={option.name} />}
              valueTemplate={(option) => <DropdownItem icon={option.icon} name={option.name} />}
              filter
            />
          </>
        )}

        {isRequiredError && !isFirstLoad && <span>{t('fieldIsRequired')}</span>}
      </div>
    </div>
  );
};

export default ChangeName;
